import { Box, capitalize } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { StyleObj } from '../../../@types';
import { JackpotProduct, JackpotReport, JackpotReportTotals } from '../../../@types/api';
import { JACKPOT_API_SERVICE } from '../../../constants/jackpot';
import { appendTotalsRow, getTotalsRowClass } from '../../../helpers';
import { usePagination } from '../../../hooks/usePagination';
import useSort from '../../../hooks/useSort';
import { createColumn } from '../../../utils';
import DateRangeSelect, { DayjsTimestamp } from '../../molecules/DateRangeSelect';
import TableTemplate from '../../templates/TableTemplate';
import { createNumberColumn } from '../../../helpers/columns';
import API_ROUTES from '../../../constants/apiRoutes';

const styles: StyleObj = {
  container: {
    position: 'relative',
    height: 'calc(100% - 200px)',
  },
};

const getMobileCardHeaderItems = (report: JackpotReportRow) => [
  { value: report.winnerName, flex: 1, align: 'left' as const },
  { value: report.isTotalsRow ? '' : capitalize(report.type) },
  { value: report.amount.toFixed(2) },
];

type JackpotReportRow = JackpotReport & {
  isTotalsRow?: boolean;
};

type Props = {
  product: JackpotProduct;
};

const JackpotReportsPage = ({ product }: Props) => {
  const [date, setDate] = useState<{ fromTimestamp: Dayjs; toTimestamp: Dayjs }>({
    fromTimestamp: dayjs().startOf('day'),
    toTimestamp: dayjs().endOf('day'),
  });

  const productQueryParams = useMemo(() => ({ product }), [product]);

  const { data, updateQueryParams, isFetching, isLoading, changeQuery } = usePagination<
    JackpotReport,
    JackpotReportTotals
  >(
    API_ROUTES.REPORTS.JACKPOTS,
    {
      page: 1,
      limit: 25,
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    },
    undefined,
    JACKPOT_API_SERVICE[product],
    undefined,
    productQueryParams
  );

  const generateTotalsRow = (totals?: JackpotReportTotals): JackpotReportRow | undefined => {
    if (totals) {
      return {
        id: 'totals',
        jackpotId: '',
        triggeredAt: '',
        amount: totals.amount,
        wonAt: '',
        createdAt: '',
        updatedAt: '',
        name: '',
        type: 'progressive',
        product,
        winnerName: String(totals.winnersCount),
        winnersCount: totals.winnersCount,
        isTotalsRow: true,
      };
    }
  };

  const totalsRow = generateTotalsRow(data?.totals);
  const aggregatedData = appendTotalsRow(data?.items, totalsRow);

  const { handleSort } = useSort(changeQuery);

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  useEffect(() => {
    changeQuery({
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    });
  }, [date, changeQuery]);

  const columns: GridColDef[] = [
    createColumn('id', 'ID', {
      sortable: false,
      renderCell: (params) => (params.row.id === 'totals' ? 'Totals' : params.value),
    }),
    createColumn('type', 'Type', {
      sortable: true,
      valueGetter: (value, row, column, apiRef) => (row.isTotalsRow ? '' : row.type),
    }),
    createColumn('name', 'Name', { sortable: true, minWidth: 150 }),
    createColumn('wonAt', 'Date and Time', {
      sortable: true,
      valueGetter: (value, row, column, apiRef) =>
        row.id === 'totals' ? '' : dayjs(value).format('DD-MM-YYYY HH:mm:ss'),
    }),
    createColumn('winnerName', 'Winner', { sortable: true }),
    createNumberColumn('amount', 'Winning Amount', {
      sortable: true,
    }),
  ];

  const handleDateChange = (newDate: DayjsTimestamp) => {
    setDate(newDate);
  };

  return (
    <Box sx={styles.container}>
      <TableTemplate
        rows={aggregatedData}
        columns={columns}
        loading={isFetching || isLoading}
        rowCount={data?.count || 0}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        getRowClassName={getTotalsRowClass}
        changeQuery={changeQuery}
        mobileCardHeaderItems={getMobileCardHeaderItems}
        customTableHeaderContent={<DateRangeSelect date={date} onDateChange={handleDateChange} />}
        showExport
      />
    </Box>
  );
};

export default JackpotReportsPage;
